import { initialsAvatar } from '../utils/avatar';
import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const AcaryaProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ pan: '', ifsc: '', bankAccount: '' });
  const [updatingPayout, setUpdatingPayout] = useState(false);
  const [showBankAcc, setShowBankAcc] = useState(false);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/acaryas/me/profile');
      if (res.data.success) {
        setProfile(res.data.data);
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to fetch your acarya profile.');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const handleUpdatePayout = async (e) => {
    e.preventDefault();
    setUpdatingPayout(true);
    try {
      const res = await api.put('/acaryas/me/payout-info', { payoutInfo: payoutForm });
      if (res.data.success) {
        showToast('success', 'Payout information updated successfully.');
        setProfile(prev => ({ ...prev, payoutInfo: res.data.data }));
        setShowPayoutModal(false);
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to update payout information.');
    } finally {
      setUpdatingPayout(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20 bg-[#0E0E26] min-h-screen">
      <div className="w-12 h-12 border-4 border-[#E8B84B]/20 border-t-[#E8B84B] rounded-full animate-spin"></div>
    </div>
  );

  if (!profile) return (
    <div className="p-8 bg-[#0E0E26] min-h-screen text-white font-sans text-center">
      <p className="text-white/40">Profile not found. Please contact administration.</p>
    </div>
  );

  return (
    <div className="p-8 bg-[#0E0E26] min-h-screen text-white font-sans">
      
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-black text-white uppercase tracking-widest">My Consultant Profile</h2>
        <p className="text-white/40 text-xs font-bold uppercase tracking-widest mt-2">View active credentials and public listing settings</p>
      </div>

      {toast && (
        <div className={`p-4 rounded-xl border mb-6 text-xs font-bold ${toast.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {toast.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Visual Profile Card */}
        <div className="bg-[#14122B] p-8 rounded-3xl border border-white/5 shadow-2xl flex flex-col items-center text-center">
          <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-[#E8B84B] shadow-inner mb-6">
            <img 
              src={profile.image || initialsAvatar(profile.name)} 
              alt={profile.name} 
              className="w-full h-full object-cover"
            />
          </div>
          <h3 className="text-xl font-bold text-white mb-1">{profile.name}</h3>
          <p className="text-[#E8B84B] text-xs font-black uppercase tracking-widest mb-4">{profile.title}</p>
          
          <div className="flex gap-2 mb-6">
            <span className="bg-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-green-500/20">
              ● Active Listing
            </span>
            <span className="bg-white/5 text-white/60 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-white/5">
              ⭐ {profile.rating || 4.8}
            </span>
          </div>

          <div className="w-full border-t border-white/5 pt-6 space-y-4 text-left">
            <div>
              <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Base Per-Minute Rates</span>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-black/25 p-3 rounded-xl text-center border border-white/5">
                  <div className="text-[10px] text-[#E8B84B] font-bold">Chat</div>
                  <div className="text-sm font-black text-white mt-1">₹{profile.pricing?.chat || 15}</div>
                </div>
                <div className="bg-black/25 p-3 rounded-xl text-center border border-white/5">
                  <div className="text-[10px] text-[#E8B84B] font-bold">Audio</div>
                  <div className="text-sm font-black text-white mt-1">₹{profile.pricing?.audio || 15}</div>
                </div>
                <div className="bg-black/25 p-3 rounded-xl text-center border border-white/5">
                  <div className="text-[10px] text-[#E8B84B] font-bold">Video</div>
                  <div className="text-sm font-black text-white mt-1">₹{profile.pricing?.video || 25}</div>
                </div>
              </div>
            </div>
            
            <div>
              <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Platform Split Share</span>
              <div className="p-3 bg-black/25 rounded-xl text-xs font-black uppercase tracking-wider text-stone-300 border border-white/5 text-center">
                💸 Platform Fee: {profile.commissionTier || 30}% / Your Share: {100 - (profile.commissionTier || 30)}%
              </div>
            </div>
          </div>
        </div>

        {/* Right Columns: Detail profile content */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Section 1: Professional Details */}
          <div className="bg-[#14122B] p-8 rounded-3xl border border-white/5 shadow-2xl">
            <h3 className="text-sm font-black uppercase text-[#E8B84B] tracking-wider mb-6">Acarya Qualifications</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Experience & Category</span>
                <p className="text-sm text-stone-200 font-bold">{profile.experience || '10+ Years'} • {profile.category || 'Acarya'}</p>
              </div>
              <div>
                <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Languages Spoken</span>
                <p className="text-sm text-stone-200 font-bold">{(profile.languagesSpoken || []).join(', ') || 'English, Hindi'}</p>
              </div>
            </div>

            <div className="mb-6">
              <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Core Competency Fields</span>
              <div className="flex gap-2 flex-wrap mt-2">
                {(profile.skills || []).map(skill => (
                  <span key={skill} className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg font-bold text-stone-300">
                    ✨ {skill}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Official Location</span>
              <p className="text-sm text-stone-200 font-bold">📍 {profile.location || 'Varanasi, India'}</p>
            </div>
          </div>

          {/* Section 2: Payout and KYC Info */}
          <div className="bg-[#14122B] p-8 rounded-3xl border border-white/5 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-sm font-black uppercase text-[#E8B84B] tracking-wider">🔒 Protected Payout Credentials</h3>
              <div className="flex gap-2">
                <button onClick={() => {
                  setPayoutForm({ 
                    pan: profile.payoutInfo?.pan || '', 
                    ifsc: profile.payoutInfo?.ifsc || '', 
                    bankAccount: profile.payoutInfo?.bankAccount || '' 
                  });
                  setShowPayoutModal(true);
                }} className="text-[9px] px-3 py-1 rounded bg-[#E8B84B]/10 border border-[#E8B84B]/20 text-[#E8B84B] font-black uppercase hover:bg-[#E8B84B] hover:text-black transition-all">
                  Update Settings
                </button>
                <span className="text-[9px] px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-black uppercase flex items-center">
                  Encrypted
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">PAN Card Number (Masked)</span>
                <p className="text-sm font-mono text-stone-300 bg-black/20 px-3 py-2 rounded-lg border border-white/5 select-none">
                  {profile.payoutInfo?.pan ? `•••••${profile.payoutInfo.pan.slice(-4)}` : '—'}
                </p>
              </div>
              <div>
                <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Bank Name & IFSC</span>
                <p className="text-sm font-bold text-stone-200">
                  {profile.payoutInfo?.ifsc ? `IFSC: ${profile.payoutInfo.ifsc.toUpperCase()}` : '—'}
                </p>
              </div>
              <div className="md:col-span-2">
                <span className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Direct Deposit Account Number (Masked)</span>
                <p className="text-sm font-mono text-stone-300 bg-black/20 px-3 py-2 rounded-lg border border-white/5 select-none">
                  {profile.payoutInfo?.bankAccount ? `••••••••••••${profile.payoutInfo.bankAccount.slice(-4)}` : '—'}
                </p>
                <p className="text-[10px] text-white/20 mt-2 italic">
                  To update payout details or governmental credentials, please contact the platform administration.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {showPayoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0E0E26] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl relative">
            <h2 className="text-xl font-bold text-white mb-4 font-serif">Update Payout Details</h2>
            <p className="text-sm text-stone-400 mb-6">Ensure your governmental and banking credentials are accurate to receive direct deposit payouts.</p>
            <form onSubmit={handleUpdatePayout} className="space-y-4">
              <div>
                <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">PAN Card Number</label>
                <input 
                  type="text" 
                  value={payoutForm.pan} 
                  onChange={(e) => setPayoutForm({...payoutForm, pan: e.target.value.toUpperCase()})}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#E8B84B] transition-colors"
                  placeholder="e.g. ABCDE1234F"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">IFSC Code</label>
                <input 
                  type="text" 
                  value={payoutForm.ifsc} 
                  onChange={(e) => setPayoutForm({...payoutForm, ifsc: e.target.value.toUpperCase()})}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-3 focus:border-[#E8B84B] transition-colors" 
                  placeholder="e.g. SBIN0001234"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Bank Account Number</label>
                <div className="relative">
                  <input 
                    type={showBankAcc ? "text" : "password"} 
                    value={payoutForm.bankAccount} 
                    onChange={(e) => setPayoutForm({...payoutForm, bankAccount: e.target.value})}
                    className="w-full !bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 pr-10 text-sm text-white focus:outline-none !focus:bg-black focus:border-[#E8B84B] transition-colors font-mono"
                    placeholder="Account Number"
                    required
                  />
                  <button 
                    type="button"
                    onClick={() => setShowBankAcc(!showBankAcc)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black hover:text-black bg-transparent border-none p-0 outline-none flex items-center justify-center"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {showBankAcc ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-white/10 mt-6">
                <button 
                  type="button" 
                  onClick={() => setShowPayoutModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 font-bold text-sm transition-all"
                  disabled={updatingPayout}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 py-2.5 rounded-xl bg-[#E8B84B] text-black font-bold text-sm hover:bg-[#b5952f] transition-all"
                  disabled={updatingPayout}
                >
                  {updatingPayout ? 'Updating...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AcaryaProfile;
